/**
 * The Ankiety tab's date range — **eight weeks, over its own floor.**
 *
 * Everything a range bar decides is `model/trendsRange`: the ceiling, "no range may end in the
 * future", the reversed and unparseable cases, the four `min`/`max` bounds and the UTC arithmetic
 * the default window is shifted by. This file is the two numbers that are this tab's own.
 *
 * ── The FLOOR is not Trendy's, and that is not a preference ──────────────────────────────────
 * Trendy refuses a window under seven days because `GET /customer/{id}/muscle-volume` divides the
 * window's set count by `spanDays / 7`, so a two-day window multiplies every muscle's figure by
 * three and a half. This tab's read divides by nothing: `GET /customer/{id}/surveys` takes no range
 * at all, and the bar filters occurrences that are already in hand. A window of one day is a real
 * question here — "what did she send on Monday" — and refusing it with a sentence about seven days
 * was a rule borrowed from arithmetic that this surface does not do.
 *
 * ── The window it OPENS on ───────────────────────────────────────────────────────────────────
 * Thirty days is four columns at a weekly cadence — too few for a trend. Eight weeks is the canon's
 * own specimen (`01-matryca.html` draws eight columns) and two months of a weekly check-in.
 *
 * ── The range does not key a request ─────────────────────────────────────────────────────────
 * So there is no `placeholderData` to keep across a range change and nothing to refetch — moving the
 * bar re-projects an answer already in hand, which is why the matrix does not flicker while the
 * trainer drags a date.
 */

import { surveyChartWindow } from '~/features/survey-report';

import { decideDateRange, defaultDateRange, TRENDS_MAX_WINDOW_DAYS } from './trendsRange';
import type { TrendsRange, TrendsRangeDeps, TrendsRangeView } from './trendsRange';

/**
 * The shortest window this tab accepts, as the span the bounds are computed from.
 *
 * Zero, because a window of ONE DAY — `from` and `to` on the same date — is the shortest thing a
 * trainer can ask for and it is a legal question. Anything below it is `from` after `to`, which is
 * refused as reversed rather than as too short.
 */
export const SURVEYS_MIN_SPAN_DAYS = 0;

/**
 * Today, and eight weeks back from it — `features/survey-report`'s ONE chart window, which the
 * review inbox opens its strip on too.
 */
export function defaultSurveysRange(today: Date): TrendsRange {
  return surveyChartWindow(today);
}

/** The shared decision, with this tab's floor. See the header for why the floor differs. */
export function decideSurveysRange(range: TrendsRange, deps: TrendsRangeDeps): TrendsRangeView {
  return decideDateRange(range, deps, {
    minSpanDays: SURVEYS_MIN_SPAN_DAYS,
    maxSpanDays: TRENDS_MAX_WINDOW_DAYS,
  });
}

/**
 * The toolbar's three ready windows (owner, 4 November 2026): four weeks, eight — the tab's own
 * default — and three months. A range the trainer typed into the two fields is none of them.
 */
export const SURVEY_RANGE_PRESETS = ['weeks4', 'weeks8', 'months3'] as const;

export type SurveyRangePreset = (typeof SURVEY_RANGE_PRESETS)[number];

/** A preset's window ending today. Three months is calendar months, clamped to the month's end. */
export function presetSurveysRange(preset: SurveyRangePreset, today: Date): TrendsRange {
  if (preset === 'weeks4') return defaultDateRange(today, 28);
  if (preset === 'weeks8') return defaultSurveysRange(today);
  const { to } = defaultDateRange(today, 0);
  const [year, month, day] = to.split('-').map(Number) as [number, number, number];
  const lastOfTarget = new Date(Date.UTC(year, month - 3, 0)).getUTCDate();
  const from = new Date(Date.UTC(year, month - 4, Math.min(day, lastOfTarget)));
  return { from: from.toISOString().slice(0, 10), to };
}

/** Which preset a range IS, or `null` for one the trainer set by hand. */
export function surveysRangePreset(range: TrendsRange, today: Date): SurveyRangePreset | null {
  return (
    SURVEY_RANGE_PRESETS.find((preset) => {
      const window = presetSurveysRange(preset, today);
      return window.from === range.from && window.to === range.to;
    }) ?? null
  );
}

/** „9 wrz – 4 lis": the range in the reader's words, on the toolbar's own segment. */
export function surveysRangeLabel(range: TrendsRange, locale: string): string {
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${day.format(new Date(`${range.from}T00:00:00Z`))} – ${day.format(new Date(`${range.to}T00:00:00Z`))}`;
}
